if (!customElements.get("tabs-component")) {
  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        tabHeader: '[role="tablist"]',
        tabPanels: ['[role="tabpanel"]'],
        tabNavs: ['[role="tab"]'],
        tabSelect: "tab-selector",
      };
      this.domNodes = queryDomNodes(this.selectors, this);

      this.Collapsible = null;
      this.selectedIndex = 0;
      this.selectedTab = this.domNodes.tabPanels[this.selectedIndex];
      this.alignmentToken = 0;
      this.alignmentFrame = null;
      this.mobileAlignmentTimer = null;
      this.pendingMobilePanel = null;
      this.mobileTriggers = [];
      this.mobileSectionNav = null;
      this.mobileSectionNavScroller = null;
      this.mobileSectionNavItems = [];
      this.mobileSectionNavHeaderObserver = null;
      this.mobileSectionNavHeaderStateObserver = null;
      this.mobileSectionNavResizeHandler = null;
      this.unsubscribeCollapsibleOpened = null;
      this.unsubscribeCollapsibleClosed = null;
    }

    connectedCallback() {
      if (!this.filterEmptyProductInfoTabs()) return;

      this.init();
      this.initProductInfoAlignment();
      this.setActiveTab(0);

      this.tabChange = new CustomEvent("tabChange", {
        bubbles: true,
        detail: { selectedTab: this.selectedTab },
      });
    }

    disconnectedCallback() {
      this.unsubscribeCollapsibleOpened && this.unsubscribeCollapsibleOpened();
      this.unsubscribeCollapsibleOpened = null;
      this.unsubscribeCollapsibleClosed && this.unsubscribeCollapsibleClosed();
      this.unsubscribeCollapsibleClosed = null;
      clearTimeout(this.mobileAlignmentTimer);
      cancelAnimationFrame(this.alignmentFrame);
      this.mobileTriggers.forEach(({ trigger, handler }) => {
        trigger.removeEventListener("click", handler, true);
      });
      this.mobileTriggers = [];
      this.destroyMobileSectionNav();
    }

    init = () => {
      this.domNodes.tabNavs.forEach((tab) => {
        tab.addEventListener("click", (e) => {
          e.preventDefault();
          const index = e.target ? Number(e.target.dataset.index) : -1;
          this.setActiveTab(index, true);
        });
      });

      this.domNodes.tabHeader &&
        this.domNodes.tabHeader.addEventListener(
          "keydown",
          this.handleKeyDown.bind(this)
        );
      this.setAccessible();
    };

    isStandardProductInfoTabs = () => {
      return Boolean(
        this.closest(".f-information-tabs") &&
          this.querySelector("collapsible-tab")
      );
    };

    hasMeaningfulProductInfoContent = (panel) => {
      const content = panel?.querySelector("collapsible-tab > [data-content]");
      if (!content) return false;

      const meaningfulElementSelector = [
        "img[src]",
        "picture source[srcset]",
        "video",
        "video source[src]",
        "iframe[src]",
        "model-viewer",
        "deferred-media",
        "[data-src]",
        "[data-srcset]",
        "[data-video-id]",
        "script[src]",
        "a[href]",
        "button:not([data-trigger])",
        "form",
        "input:not([type='hidden'])",
        "select",
        "textarea",
        "product-combo",
        "product-recommendations",
        "flickity-component",
        "grid-list",
        "#shopify-product-reviews[data-id]",
        ".yotpo-widget-instance",
        ".shopify-app-block",
        "[data-shopify-app-block]",
        "[id^='shopify-block-']",
        ".yotpo",
        ".jdgm-widget",
      ].join(",");

      if (content.querySelector(meaningfulElementSelector)) return true;

      const textContent = content.cloneNode(true);
      textContent
        .querySelectorAll(
          "script, style, template, noscript, [data-trigger], summary"
        )
        .forEach((element) => element.remove());

      return textContent.textContent.replace(/\s+/g, " ").trim().length > 0;
    };

    filterEmptyProductInfoTabs = () => {
      if (!this.isStandardProductInfoTabs()) return true;

      const originalTabNavs = Array.from(this.domNodes.tabNavs || []);
      const originalTabPanels = Array.from(this.domNodes.tabPanels || []);
      const validTabNavs = [];
      const validTabPanels = [];
      const validOriginalIndexes = [];

      originalTabPanels.forEach((panel, index) => {
        const tab = originalTabNavs[index];
        const hasContent = this.hasMeaningfulProductInfoContent(panel);

        if (hasContent && tab) {
          tab.hidden = false;
          tab.removeAttribute("aria-hidden");
          panel.removeAttribute("data-tab-empty");
          validTabNavs.push(tab);
          validTabPanels.push(panel);
          validOriginalIndexes.push(index);
          return;
        }

        if (tab) {
          tab.hidden = true;
          tab.setAttribute("aria-hidden", "true");
          tab.setAttribute("tabindex", "-1");
        }
        panel.hidden = true;
        panel.setAttribute("aria-hidden", "true");
        panel.setAttribute("data-tab-empty", "true");
      });

      this.domNodes.tabNavs = validTabNavs;
      this.domNodes.tabPanels = validTabPanels;
      this.selectedIndex = 0;
      this.selectedTab = validTabPanels[0] || null;

      const select = this.domNodes.tabSelect?.querySelector("select");
      if (select) {
        Array.from(select.options).forEach((option, index) => {
          const isValid = validOriginalIndexes.includes(index);
          option.hidden = !isValid;
          option.disabled = !isValid;
          if (isValid) option.value = validOriginalIndexes.indexOf(index);
        });
      }

      if (!validTabPanels.length) {
        const section = this.closest(".f-information-tabs");
        if (section) section.hidden = true;
        return false;
      }

      const validCollapsibles = validTabPanels
        .map((panel) => panel.querySelector("collapsible-tab"))
        .filter(Boolean);
      const hasOpenValidPanel = validCollapsibles.some(
        (panel) => this.isCollapsiblePanelOpen(panel)
      );

      if (!hasOpenValidPanel && validCollapsibles[0]) {
        if (typeof validCollapsibles[0].open === "function") {
          validCollapsibles[0].open();
        } else {
          validCollapsibles[0].setAttribute("open", "true");
        }
      }

      return true;
    };

    isCollapsiblePanelOpen = (panel) => {
      if (!panel) return false;

      const content = panel.querySelector("[data-content]");
      const trigger = panel.querySelector("[data-trigger]");
      const isSelected =
        panel.selected === true || panel.classList.contains("is-selected");
      const isExpanded = trigger?.getAttribute("aria-expanded") === "true";
      const hasVisibleContent =
        !content || content.getBoundingClientRect().height > 1;

      // A stale close transition can leave the requested panel selected and
      // aria-expanded while its content is still at 0px. Treat that as closed
      // so the quiet-window recovery re-opens the latest requested panel.
      return isSelected && isExpanded && hasVisibleContent;
    };

    prepareMobilePanelAlignment = (panel) => {
      if (!panel) return;

      this.alignmentToken += 1;
      clearTimeout(this.mobileAlignmentTimer);
      const token = this.alignmentToken;
      const content = panel.querySelector("[data-content]");
      this.pendingMobilePanel = panel;
      this.mobileAlignmentTimer = window.setTimeout(() => {
        this.alignPendingMobilePanel(panel, token);
      }, this.getTransitionDuration(content) + 50);
    };

    getTransitionDuration = (element) => {
      if (!element) return 0;

      const style = window.getComputedStyle(element);
      const toMilliseconds = (value) => {
        const parsed = Number.parseFloat(value) || 0;
        return value.trim().endsWith("ms") ? parsed : parsed * 1000;
      };
      const durations = style.transitionDuration.split(",").map(toMilliseconds);
      const delays = style.transitionDelay.split(",").map(toMilliseconds);

      return durations.reduce((maximum, duration, index) => {
        const delay = delays[index % delays.length] || 0;
        return Math.max(maximum, duration + delay);
      }, 0);
    };

    initProductInfoAlignment = () => {
      if (!this.isStandardProductInfoTabs()) return;

      this.mobileTriggers = Array.from(
        this.querySelectorAll(
          '[role="tabpanel"]:not([data-tab-empty="true"]) collapsible-tab > [data-trigger]'
        )
      ).map((trigger) => {
        const handler = () => {
          if (!window.matchMedia("(max-width: 767.98px)").matches) return;

          const panel = trigger.closest("collapsible-tab");
          const willOpen = !this.isCollapsiblePanelOpen(panel);

          if (!willOpen || !panel) {
            this.alignmentToken += 1;
            clearTimeout(this.mobileAlignmentTimer);
            this.pendingMobilePanel = null;
            return;
          }

          this.prepareMobilePanelAlignment(panel);
        };

        // Capture the pre-click panel state before collapsible-tab toggles it.
        trigger.addEventListener("click", handler, true);
        return { trigger, handler };
      });

      this.initMobileSectionNav();

      if (window.FoxThemeEvents && window.FoxThemeEvents.subscribe) {
        this.unsubscribeCollapsibleOpened = window.FoxThemeEvents.subscribe(
          "ON_COLLAPSIBLE_TAB_OPENED",
          (panel) => {
            const index = this.mobileSectionNavItems.findIndex(
              ({ trigger }) => trigger.closest("collapsible-tab") === panel
            );
            if (index !== -1) this.setMobileSectionNavActive(index, true);

            // Keep the quiet-window verification scheduled by
            // prepareMobilePanelAlignment. An older transition can emit an
            // opened event and then be closed by a newer rapid tap; aligning
            // immediately here would clear the recovery target too early.
          }
        );

        this.unsubscribeCollapsibleClosed = window.FoxThemeEvents.subscribe(
          "ON_COLLAPSIBLE_TAB_CLOSED",
          (panel) => {
            const closedIndex = this.mobileSectionNavItems.findIndex(
              ({ trigger }) => trigger.closest("collapsible-tab") === panel
            );
            const currentIndex = this.mobileSectionNavItems.findIndex(
              ({ button }) => button.getAttribute("aria-current") === "true"
            );

            // Opening another item closes the old accordion at the same time.
            // Do not clear the newly selected navigation item when that old
            // panel's delayed close event arrives.
            if (closedIndex !== currentIndex) return;

            const openIndex = this.mobileSectionNavItems.findIndex(
              ({ trigger }) => trigger.getAttribute("aria-expanded") === "true"
            );
            this.setMobileSectionNavActive(openIndex, false);
          }
        );
      }
    };

    initMobileSectionNav = () => {
      if (this.mobileSectionNav || !this.mobileTriggers.length) return;

      const blocks = this.querySelector(".f-product-tabs__blocks");
      if (!blocks) return;

      const nav = document.createElement("nav");
      const scroller = document.createElement("div");
      nav.className = "f-tabs__mobile-section-nav";
      nav.setAttribute(
        "aria-label",
        this.domNodes.tabHeader?.getAttribute("aria-label") ||
          "Product information sections"
      );
      scroller.className = "f-tabs__mobile-section-nav-scroller";
      nav.appendChild(scroller);

      this.mobileSectionNavItems = this.mobileTriggers
        .map(({ trigger }, triggerIndex) => {
          const panel = trigger.closest('[role="tabpanel"]');
          if (!panel || !panel.id) return null;

          const navIndex = scroller.children.length;
          const button = document.createElement("button");
          const label = trigger.querySelector("span")?.textContent?.trim();
          button.type = "button";
          button.className = "f-tabs__mobile-section-nav-button focus-inset";
          button.textContent = label || trigger.textContent.trim();
          button.setAttribute("aria-controls", panel.id);
          button.setAttribute(
            "aria-expanded",
            trigger.getAttribute("aria-expanded") === "true"
              ? "true"
              : "false"
          );
          button.dataset.index = triggerIndex;

          const clickHandler = () => {
            const willOpen = !this.isCollapsiblePanelOpen(
              trigger.closest("collapsible-tab")
            );
            this.setMobileSectionNavActive(navIndex, true);

            if (willOpen) {
              const collapsible = trigger.closest("collapsible-tab");
              this.prepareMobilePanelAlignment(collapsible);
              if (typeof collapsible?.open === "function") {
                collapsible.open();
              } else {
                trigger.click();
              }
              return;
            }

            clearTimeout(this.mobileAlignmentTimer);
            this.pendingMobilePanel = null;
            this.scheduleAlignment(panel, true);
          };

          const keydownHandler = (event) => {
            const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
            if (!keys.includes(event.key)) return;

            event.preventDefault();
            const lastIndex = this.mobileSectionNavItems.length - 1;
            const currentIndex = this.mobileSectionNavItems.findIndex(
              ({ button: itemButton }) => itemButton === event.currentTarget
            );
            if (currentIndex === -1) return;
            let focusIndex = currentIndex;

            if (event.key === "Home") focusIndex = 0;
            if (event.key === "End") focusIndex = lastIndex;
            if (event.key === "ArrowLeft") {
              focusIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
            }
            if (event.key === "ArrowRight") {
              focusIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
            }

            this.setMobileSectionNavFocus(focusIndex);
          };

          button.addEventListener("click", clickHandler);
          button.addEventListener("keydown", keydownHandler);
          scroller.appendChild(button);

          return { button, clickHandler, keydownHandler, panel, trigger };
        })
        .filter(Boolean);

      if (!this.mobileSectionNavItems.length) return;

      blocks.before(nav);
      this.classList.add("f-tabs--mobile-section-nav-ready");
      this.mobileSectionNav = nav;
      this.mobileSectionNavScroller = scroller;
      this.initMobileSectionNavOffset();

      const openIndex = this.mobileSectionNavItems.findIndex(
        ({ trigger }) => trigger.getAttribute("aria-expanded") === "true"
      );
      this.setMobileSectionNavActive(openIndex, false);
    };

    initMobileSectionNavOffset = () => {
      const siteHeader = document.querySelector(".header-sticky");
      if (!siteHeader || !this.mobileSectionNav) return;
      const siteHeaderSurface =
        siteHeader.querySelector(".header") || siteHeader;

      this.mobileSectionNavResizeHandler = () => {
        requestAnimationFrame(() => {
          if (!this.mobileSectionNav) return;

          const headerHeight = siteHeaderSurface.getBoundingClientRect().height;
          if (headerHeight > 0) {
            this.mobileSectionNav.style.setProperty(
              "--f-tabs-mobile-header-offset",
              `${headerHeight}px`
            );
            this.domNodes.tabHeader?.style.setProperty(
              "--f-tabs-desktop-header-offset",
              `${headerHeight}px`
            );
          }
        });
      };
      this.mobileSectionNavResizeHandler();

      if (window.ResizeObserver) {
        this.mobileSectionNavHeaderObserver = new window.ResizeObserver(
          this.mobileSectionNavResizeHandler
        );
        this.mobileSectionNavHeaderObserver.observe(siteHeaderSurface);
      }
      if (window.MutationObserver) {
        this.mobileSectionNavHeaderStateObserver = new window.MutationObserver(
          this.mobileSectionNavResizeHandler
        );
        this.mobileSectionNavHeaderStateObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
      window.addEventListener("resize", this.mobileSectionNavResizeHandler);
    };

    destroyMobileSectionNav = () => {
      this.mobileSectionNavHeaderObserver?.disconnect();
      this.mobileSectionNavHeaderObserver = null;
      this.mobileSectionNavHeaderStateObserver?.disconnect();
      this.mobileSectionNavHeaderStateObserver = null;
      if (this.mobileSectionNavResizeHandler) {
        window.removeEventListener(
          "resize",
          this.mobileSectionNavResizeHandler
        );
      }
      this.mobileSectionNavResizeHandler = null;
      this.mobileSectionNavItems.forEach(
        ({ button, clickHandler, keydownHandler }) => {
          button.removeEventListener("click", clickHandler);
          button.removeEventListener("keydown", keydownHandler);
        }
      );
      this.mobileSectionNav?.remove();
      this.classList.remove("f-tabs--mobile-section-nav-ready");
      this.mobileSectionNav = null;
      this.mobileSectionNavScroller = null;
      this.mobileSectionNavItems = [];
    };

    setMobileSectionNavFocus = (index) => {
      const item = this.mobileSectionNavItems[index];
      if (!item) return;

      this.mobileSectionNavItems.forEach(({ button }, itemIndex) => {
        button.tabIndex = itemIndex === index ? 0 : -1;
      });
      item.button.focus();
      this.scrollMobileSectionNavTo(index);
    };

    setMobileSectionNavActive = (index, scrollIntoView) => {
      const focusIndex = index === -1 ? 0 : index;

      this.mobileSectionNavItems.forEach(({ button }, itemIndex) => {
        const isActive = itemIndex === index;
        button.classList.toggle("is-active", isActive);
        button.tabIndex = itemIndex === focusIndex ? 0 : -1;

        if (isActive) {
          button.setAttribute("aria-current", "true");
          button.setAttribute("aria-expanded", "true");
        } else {
          button.removeAttribute("aria-current");
          button.setAttribute("aria-expanded", "false");
        }
      });

      if (scrollIntoView && index !== -1) {
        this.scrollMobileSectionNavTo(index);
      }
    };

    scrollMobileSectionNavTo = (index) => {
      if (
        !window.matchMedia("(max-width: 767.98px)").matches ||
        !this.mobileSectionNavScroller
      ) {
        return;
      }

      const item = this.mobileSectionNavItems[index]?.button;
      if (!item) return;

      const left =
        item.offsetLeft -
        (this.mobileSectionNavScroller.clientWidth - item.offsetWidth) / 2;
      this.mobileSectionNavScroller.scrollTo({
        left: Math.max(0, left),
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    };

    alignPendingMobilePanel = (panel, token, allowRecovery = true) => {
      if (
        token !== this.alignmentToken ||
        panel !== this.pendingMobilePanel ||
        !panel.isConnected
      ) {
        return;
      }

      if (!this.isCollapsiblePanelOpen(panel)) {
        // A later close transition from a prior rapid tap can win after the
        // requested panel was opened. Re-assert only the latest request once
        // the previous transition has settled, then verify before aligning.
        if (allowRecovery && typeof panel.open === "function") {
          panel.open();
          const content = panel.querySelector("[data-content]");
          this.mobileAlignmentTimer = window.setTimeout(() => {
            this.alignPendingMobilePanel(panel, token, false);
          }, this.getTransitionDuration(content) + 50);
        }
        return;
      }

      clearTimeout(this.mobileAlignmentTimer);
      this.pendingMobilePanel = null;
      this.scheduleAlignment(panel, true, token);
    };

    getOcclusionBottom = (mobile) => {
      const siteHeader = document.querySelector(".header-sticky");
      const siteHeaderSurface =
        siteHeader?.querySelector(".header") || siteHeader;
      const siteHeaderRect =
        siteHeaderSurface && siteHeaderSurface.getBoundingClientRect();
      const siteHeaderBottom =
        siteHeaderRect &&
        siteHeaderRect.bottom > 0 &&
        siteHeaderRect.top < window.innerHeight
          ? siteHeaderRect.bottom
          : 0;

      if (mobile) {
        const mobileNavRect =
          this.mobileSectionNav &&
          this.mobileSectionNav.getBoundingClientRect();
        const mobileNavBottom =
          mobileNavRect &&
          window.getComputedStyle(this.mobileSectionNav).display !== "none" &&
          mobileNavRect.bottom > 0 &&
          mobileNavRect.top < window.innerHeight
            ? mobileNavRect.bottom
            : 0;

        return Math.max(0, siteHeaderBottom, mobileNavBottom);
      }

      const tabHeader = this.domNodes.tabHeader;
      if (!tabHeader) return Math.max(0, siteHeaderBottom);

      const tabHeaderStyle = window.getComputedStyle(tabHeader);
      const stickyTop = Number.parseFloat(tabHeaderStyle.top) || 0;
      const tabHeaderBottom = ["sticky", "fixed"].includes(
        tabHeaderStyle.position
      )
        ? stickyTop + tabHeader.getBoundingClientRect().height
        : 0;

      // The product tab bar overlays the site header, so use the lower
      // visible edge instead of incorrectly adding both heights.
      return Math.max(0, siteHeaderBottom, tabHeaderBottom);
    };

    scheduleAlignment = (target, mobile, token = ++this.alignmentToken) => {
      cancelAnimationFrame(this.alignmentFrame);
      this.alignmentFrame = requestAnimationFrame(() => {
        this.alignmentFrame = requestAnimationFrame(() => {
          if (token !== this.alignmentToken || !target || !target.isConnected) {
            return;
          }

          const top =
            target.getBoundingClientRect().top +
            window.scrollY -
            this.getOcclusionBottom(mobile) -
            8;

          window.scrollTo({
            top: Math.max(0, top),
            // Mobile accordion panels can change the document height by
            // thousands of pixels while closing the previous panel. Native
            // smooth scrolling is interrupted by that scroll anchoring, so
            // align only after the layout settles and jump to the stable top.
            behavior: mobile ||
              window.matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth",
          });
        });
      });
    };

    setActiveTab = (tabIndex, alignAfterUserAction = false) => {
      const { tabNavs, tabPanels, tabSelect } = this.domNodes;
      if (tabIndex !== -1) {
        const newHeader = tabNavs && tabNavs[tabIndex];
        const newTab = tabPanels && tabPanels[tabIndex];
        this.setAttribute("data-selected", tabIndex);

        tabNavs.forEach((nav) => nav.setAttribute("aria-selected", false));
        this.selectedTab && this.selectedTab.setAttribute("hidden", "");

        if (newHeader) {
          newHeader.setAttribute("aria-selected", true);
        }

        newTab && newTab.removeAttribute("hidden");

        this.selectedIndex = tabIndex;
        this.selectedTab = newTab;

        this.dispatchEvent(
          new CustomEvent("tabChange", {
            bubbles: true,
            detail: { selectedIndex: tabIndex, selectedTab: newTab },
          })
        );

        if (this.isMobile && this.isMobile.matches && this.Collapsible) {
          this.Collapsible.toggle(this.Collapsible.els[tabIndex]);
        }

        if (tabSelect) {
          tabSelect.querySelector("select").value = tabIndex;
        }

        const motionEls = newTab.querySelectorAll("motion-element");
        motionEls.forEach((motionEl) => {
          typeof motionEl.refreshAnimation === "function" &&
            motionEl.refreshAnimation();
        });

        const gridListEls = newTab.querySelectorAll("grid-list");
        gridListEls.forEach((gridList) => {
          typeof gridList.showGridItems === "function" &&
            gridList.showGridItems();
        });

        const sliderEls = newTab.querySelectorAll("flickity-component");
        sliderEls &&
          sliderEls.forEach((sliderEl) => {
            /**
             * Because we set groupCells: true so we need to destroy & re-init instead of call update
             * To avoid slide can't draggable on hidden tab.
             */
            if (tabIndex != 0 && sliderEl.isReInitSliderInTab !== true) {
              typeof sliderEl.destroy === "function" && sliderEl.destroy();
              typeof sliderEl.init === "function" && sliderEl.init();
            } else {
              typeof sliderEl.update === "function" && sliderEl.update();
              if (sliderEl.slider) {
                sliderEl.slider.calcArrowsPos();
              }
            }

            sliderEl.isReInitSliderInTab = true;
          });

        if (
          alignAfterUserAction &&
          newTab &&
          this.isStandardProductInfoTabs()
        ) {
          this.scheduleAlignment(newTab, false);
        }
      }
    };

    setAccessible() {
      const { tabNavs, tabPanels } = this.domNodes;

      tabNavs &&
        tabNavs.forEach((tab, index) => {
          if (tab.id) tabPanels[index].setAttribute("aria-labelledby", tab.id);
          tab.setAttribute("aria-selected", index === 0);
          tab.setAttribute("data-index", index);
          if (index !== 0) {
            tab.setAttribute("tabindex", -1);
          }
        });
      tabPanels.forEach((panel, index) => {
        if (panel.id && tabNavs.length > 0) {
          tabNavs[index].setAttribute("aria-controls", panel.id);
        }

        panel.setAttribute("tabindex", 0);
      });
    }

    handleKeyDown(e) {
      const { tabNavs } = this.domNodes;
      if (e.keyCode === 39 || e.keyCode === 37) {
        tabNavs[this.selectedIndex].setAttribute("tabindex", -1);
        if (e.keyCode === 39) {
          this.selectedIndex++;
          // If we're at the end, go to the start
          if (this.selectedIndex >= tabNavs.length) {
            this.selectedIndex = 0;
          }
          // Move left
        } else if (e.keyCode === 37) {
          this.selectedIndex--;
          // If we're at the start, move to the end
          if (this.selectedIndex < 0) {
            this.selectedIndex = tabNavs.length - 1;
          }
        }

        tabNavs[this.selectedIndex].setAttribute("tabindex", 0);
        tabNavs[this.selectedIndex].focus();
      }
    }

    getSelected() {
      return {
        index: this.selectedIndex,
        element: this.selectedTab,
      };
    }
  }

  customElements.define("tabs-component", Tabs);
}

if (!customElements.get("tab-selector")) {
  class TabSelector extends HTMLElement {
    constructor() {
      super();

      this.tabs = this.closest("tabs-component");

      this.querySelector("select").addEventListener("change", (e) => {
        this.tabs.setActiveTab(e.target.value, true);
      });
    }
  }
  customElements.define("tab-selector", TabSelector);
}
